import { useState, useEffect } from "react";
import styles from "./CommunityMenuPanel.module.css";
import { FaHome, FaAngleDown, FaAngleRight } from "react-icons/fa";
import { MdDragIndicator } from "react-icons/md";
import { ENDPOINTS } from "../../utils/endpoints";
import useDashboardStore from "../../store/dashboardStore";
import chatStore from "../../store/chatStore";
import api from "../../services/api";
import BadgeCount from "../ChatPanel/BadgeCount";
import useAuthStore from "../../store/authStore";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Sortable Section Component
const SortableSection = ({
  sectionData,
  channelList,
  isOpen,
  onToggle,
  selectedChannel,
  onChannelSelect,
  userJoinedChannels,
  debouncedUnreadCounts,
  isReorderMode,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sectionData.id, disabled: !isReorderMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className={styles.menuItem}
        onClick={!isReorderMode ? onToggle : undefined}
        {...(isReorderMode ? { ...attributes, ...listeners } : {})}
        style={{ cursor: isReorderMode ? "grab" : "pointer" }}
      >
        <span>{sectionData.title}</span>
        <div className={styles.sectionActions}>
          {isReorderMode && <MdDragIndicator className={styles.dragHandle} />}
          {!isReorderMode &&
            (!isOpen ? (
              <FaAngleDown className={styles.arrow} />
            ) : (
              <FaAngleRight className={styles.arrow} />
            ))}
        </div>
      </div>
      {!isOpen && !isReorderMode && (
        <div className={styles.subMenu}>
          {channelList.map((channel) => (
            <div
              key={channel.id}
              className={
                selectedChannel && selectedChannel.id === channel.id
                  ? styles.subMenuItemSelected
                  : styles.subMenuItem
              }
              onClick={() => onChannelSelect(channel)}
            >
              {channel.name}{" "}
              {userJoinedChannels.find((e) => e.channel_id === channel.id) &&
                debouncedUnreadCounts[channel.id] > 0 && (
                  <BadgeCount count={debouncedUnreadCounts[channel.id]} />
                )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Sortable Channel Component
const SortableChannel = ({
  channel,
  isSelected,
  onClick,
  unreadCount,
  isReorderMode,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: channel.id, disabled: !isReorderMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isReorderMode ? "grab" : "pointer",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={isSelected ? styles.subMenuItemSelected : styles.subMenuItem}
      onClick={!isReorderMode ? onClick : undefined}
      {...(isReorderMode ? { ...attributes, ...listeners } : {})}
    >
      {channel.name}
      {unreadCount > 0 && !isReorderMode && <BadgeCount count={unreadCount} />}
      {isReorderMode && (
        <MdDragIndicator className={styles.channelDragHandle} />
      )}
    </div>
  );
};

const RenderWelcomeSection = (community) => {
  const { setIsCommunityContentScreen } = useDashboardStore();

  const hasContent =
    community?.content_title ||
    community?.community_title ||
    community?.content_primary_text ||
    community?.content_secondary_text ||
    community?.content_banner ||
    community?.content_image ||
    (community?.video_links && community.video_links.length > 0);

  if (!hasContent) return null;

  return (
    <div
      className={styles.menuItem}
      onClick={() => {
        setIsCommunityContentScreen();
      }}
    >
      <span>{community.community_title || "Academy"}</span>
      <FaAngleRight className={styles.arrow} />
    </div>
  );
};

const CommunityMenuPanel = () => {
  const { user } = useAuthStore();
  const {
    communityIndex,
    communities,
    channels,
    setChannels,
    setAllChannels,
    selectedChannel,
    setSelectedChannel,
    setIsChatScreen,
    setIsUpcomingLiveSessionScreen,
    setIsCommunityContentScreen,
    setIsWeeklyLinksScreen,
    setIsReplaySessionsScreen,
    userJoinedChannels,
  } = useDashboardStore();
  const { unreadChatCounts } = chatStore();
  const [openSections, setOpenSections] = useState({});
  const [reorderType, setReorderType] = useState(null);
  const [allSections, setAllSections] = useState([]);
  const [channelSearch, setChannelSearch] = useState("");

  // Debounced unread counts
  const [debouncedUnreadCounts, setDebouncedUnreadCounts] =
    useState(unreadChatCounts);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedUnreadCounts(unreadChatCounts);
    }, 300);

    return () => clearTimeout(handler);
  }, [unreadChatCounts]);

  // Fetch all sections
  useEffect(() => {
    const fetchSections = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_SECTIONS);
        if (data.success) {
          setAllSections(data.sections);
        }
      } catch (err) {
        console.error("Failed to fetch sections", err);
      }
    };

    fetchSections();
  }, []);

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_CHANNELS);
        if (data.success) {
          const selectedCommunity = communities[communityIndex];
          const filtered = data.channels.filter(
            (channel) => channel.community === selectedCommunity?.name,
          );
          setChannels(filtered);
          setAllChannels(data.channels);

          setTimeout(() => {
            const lastScreen = localStorage.getItem("lastScreen");
            if (lastScreen === "ChatScreen") {
              const selectedChannel = JSON.parse(
                localStorage.getItem("selectedChannel") || "{}",
              );
              if (selectedChannel && Object.keys(selectedChannel).length > 0) {
                const newChannel = filtered.find(
                  (e) => e.id === selectedChannel.id,
                );
                if (newChannel) {
                  setSelectedChannel(newChannel);
                  setIsChatScreen();
                } else {
                  localStorage.removeItem("selectedChannel");
                }
              }
            } else if (lastScreen === "UpcomingLiveSessionScreen") {
              const communityId = parseInt(
                localStorage.getItem("communityId"),
                10,
              );
              if (communityId === communities[communityIndex].id) {
                setIsUpcomingLiveSessionScreen();
              } else {
                localStorage.removeItem("communityId");
              }
            } else if (lastScreen === "CommunityContentScreen") {
              setIsCommunityContentScreen();
            } else if (lastScreen === "WeeklyLinksScreen") {
              const communityId = parseInt(
                localStorage.getItem("communityId"),
                10,
              );
              if (communityId === communities[communityIndex].id) {
                setIsWeeklyLinksScreen();
              } else {
                localStorage.removeItem("communityId");
              }
            } else if (lastScreen === "ReplaySessionsScreen") {
              const communityId = parseInt(
                localStorage.getItem("communityId"),
                10,
              );
              if (communityId === communities[communityIndex].id) {
                setIsReplaySessionsScreen();
              } else {
                localStorage.removeItem("communityId");
              }
            }
          }, 500);
        }
      } catch (err) {
        console.error("Failed to fetch channels", err);
      }
    };

    if (communities.length && communityIndex !== null) {
      setChannelSearch(""); // clear search when community changes
      fetchChannels();
    }
  }, [
    communityIndex,
    communities,
    setChannels,
    setAllChannels,
    setSelectedChannel,
    setIsChatScreen,
    setIsUpcomingLiveSessionScreen,
    setIsWeeklyLinksScreen,
    setIsReplaySessionsScreen,
  ]);

  // Group channels by section and map to section objects
  const groupedBySection = channels.reduce((acc, channel) => {
    const sectionTitle = channel.section || "General";
    if (!acc[sectionTitle]) acc[sectionTitle] = [];
    acc[sectionTitle].push(channel);
    return acc;
  }, {});

  // Sort channels by sort_order within each section
  Object.keys(groupedBySection).forEach((sectionTitle) => {
    groupedBySection[sectionTitle].sort((a, b) => {
      return (a.sort_order ?? 0) - (b.sort_order ?? 0);
    });
  });

  // Create ordered sections array with full section data
  const orderedSections = allSections
    .filter((section) => groupedBySection[section.title])
    .map((section) => ({
      ...section,
      channels: groupedBySection[section.title],
    }));

  // Handle section reordering
  const handleSectionDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = orderedSections.findIndex((s) => s.id === active.id);
    const newIndex = orderedSections.findIndex((s) => s.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      console.error("Invalid drag indices", { oldIndex, newIndex });
      return;
    }

    const reorderedSections = arrayMove(orderedSections, oldIndex, newIndex);

    setAllSections(reorderedSections);

    try {
      const orders = reorderedSections.map((section, index) => ({
        id: section.id,
        sort_order: index,
      }));

      // console.log("Reordering sections:", orders);

      await api.post(ENDPOINTS.REORDER_SECTIONS, { orders });

      const [sectionsData, channelsData] = await Promise.all([
        api.get(ENDPOINTS.GET_ALL_SECTIONS),
        api.get(ENDPOINTS.GET_ALL_CHANNELS),
      ]);

      if (sectionsData.data.success) {
        setAllSections(sectionsData.data.sections);
      }

      if (channelsData.data.success) {
        const selectedCommunity = communities[communityIndex];
        const filtered = channelsData.data.channels.filter(
          (channel) => channel.community === selectedCommunity?.name,
        );
        setChannels(filtered);
        setAllChannels(channelsData.data.channels);
      }
    } catch (err) {
      console.error("Failed to reorder sections", err);
      // Revert on error - refetch original data
      const { data } = await api.get(ENDPOINTS.GET_ALL_SECTIONS);
      if (data.success) {
        setAllSections(data.sections);
      }
    }
  };

  // Handle channel reordering
  const handleChannelDragEnd = async (event, sectionTitle) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const channelList = groupedBySection[sectionTitle];
    const oldIndex = channelList.findIndex((c) => c.id === active.id);
    const newIndex = channelList.findIndex((c) => c.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedChannels = arrayMove(channelList, oldIndex, newIndex);

    // Update local state immediately - DON'T wait for backend
    const updatedChannels = channels.map((channel) => {
      const newIndex = reorderedChannels.findIndex((c) => c.id === channel.id);
      if (newIndex !== -1) {
        return { ...channel, sort_order: newIndex };
      }
      return channel;
    });

    setChannels(updatedChannels);
    setAllChannels(updatedChannels);

    try {
      const orders = reorderedChannels.map((item, index) => ({
        id: item.id,
        sort_order: index,
      }));

      // console.log("Reordering channels:", orders);

      // Just send to backend - NO REFETCH
      await api.post(ENDPOINTS.REORDER_CHANNELS, { orders });
    } catch (err) {
      console.error("Failed to reorder channels", err);
    }
  };

  const handleChannelSelect = (channel) => {
    setSelectedChannel(channel);
    setIsChatScreen();
  };

  return (
    <div className={styles.panelWrapper}>
      <div className={styles.menuItem}>
        <FaHome className={styles.icon} />
        <span>{communities[communityIndex].name}</span>
      </div>
      <div className={styles.subMenu}>
        <div className={styles.subMenuItem}>
          {communities[communityIndex].members_count} Members Community
        </div>
      </div>

      {/* Per-community channel search */}
      <div className={styles.searchBar}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search channels…"
          value={channelSearch}
          onChange={(e) => setChannelSearch(e.target.value)}
        />
        {channelSearch && (
          <button
            className={styles.searchClearBtn}
            onClick={() => setChannelSearch("")}
          >
            ✕
          </button>
        )}
      </div>

      {RenderWelcomeSection(communities[communityIndex])}

      {/* Reorder controls - Admin only */}
      {user?.roles[0] === "administrator" && (
        <div className={styles.reorderControls}>
          <button
            className={`${styles.reorderButton} ${
              reorderType === "sections" ? styles.active : ""
            }`}
            onClick={() => {
              if (reorderType === "sections") {
                setReorderType(null);
              } else {
                setReorderType("sections");
              }
            }}
          >
            {reorderType === "sections" ? "✓" : "⋮⋮"} Sections
          </button>
          <button
            className={`${styles.reorderButton} ${
              reorderType === "channels" ? styles.active : ""
            }`}
            onClick={() => {
              if (reorderType === "channels") {
                setReorderType(null);
              } else {
                setReorderType("channels");
              }
            }}
          >
            {reorderType === "channels" ? "✓" : "⋮⋮"} Channels
          </button>
        </div>
      )}

      {/* Flat search results (shown only while searching, hides grouping) */}
      {channelSearch.trim() ? (
        (() => {
          const q = channelSearch.trim().toLowerCase();
          const matched = channels.filter((ch) =>
            ch.name?.toLowerCase().includes(q)
          );
          return matched.length === 0 ? (
            <p className={styles.searchNoResults}>No channels found</p>
          ) : (
            <div className={styles.searchResultsList}>
              {matched.map((channel) => (
                <div
                  key={channel.id}
                  className={
                    selectedChannel && selectedChannel.id === channel.id
                      ? styles.subMenuItemSelected
                      : styles.subMenuItem
                  }
                  onClick={() => handleChannelSelect(channel)}
                >
                  {channel.name}
                  {userJoinedChannels.find(
                    (e) => e.channel_id === channel.id
                  ) &&
                    debouncedUnreadCounts[channel.id] > 0 && (
                      <BadgeCount count={debouncedUnreadCounts[channel.id]} />
                    )}
                </div>
              ))}
            </div>
          );
        })()
      ) : (
      /* Sections with drag and drop */
      reorderType === "sections" ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleSectionDragEnd}
        >
          <SortableContext
            items={orderedSections.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {orderedSections.map((section) => (
              <SortableSection
                key={section.id}
                sectionData={section}
                channelList={section.channels}
                isOpen={openSections[section.title]}
                onToggle={() =>
                  setOpenSections((prev) => ({
                    ...prev,
                    [section.title]: !prev[section.title],
                  }))
                }
                selectedChannel={selectedChannel}
                onChannelSelect={handleChannelSelect}
                userJoinedChannels={userJoinedChannels}
                debouncedUnreadCounts={debouncedUnreadCounts}
                isReorderMode={reorderType === "sections"}
              />
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        orderedSections.map((section) => (
          <div key={section.id}>
            <div
              className={styles.menuItem}
              onClick={() =>
                setOpenSections((prev) => ({
                  ...prev,
                  [section.title]: !prev[section.title],
                }))
              }
            >
              <span>{section.title}</span>
              {!openSections[section.title] ? (
                <FaAngleDown className={styles.arrow} />
              ) : (
                <FaAngleRight className={styles.arrow} />
              )}
            </div>
            {!openSections[section.title] && (
              <div className={styles.subMenu}>
                {reorderType === "channels" ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) =>
                      handleChannelDragEnd(event, section.title)
                    }
                  >
                    <SortableContext
                      items={section.channels.map((c) => c.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {section.channels.map((channel) => (
                        <SortableChannel
                          key={channel.id}
                          channel={channel}
                          isSelected={
                            selectedChannel && selectedChannel.id === channel.id
                          }
                          onClick={() => handleChannelSelect(channel)}
                          unreadCount={
                            userJoinedChannels.find(
                              (e) => e.channel_id === channel.id,
                            )
                              ? debouncedUnreadCounts[channel.id] || 0
                              : 0
                          }
                          isReorderMode={reorderType === "channels"}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  section.channels.map((channel) => (
                    <div
                      key={channel.id}
                      className={
                        selectedChannel && selectedChannel.id === channel.id
                          ? styles.subMenuItemSelected
                          : styles.subMenuItem
                      }
                      onClick={() => handleChannelSelect(channel)}
                    >
                      {channel.name}{" "}
                      {userJoinedChannels.find(
                        (e) => e.channel_id === channel.id,
                      ) &&
                        debouncedUnreadCounts[channel.id] > 0 && (
                          <BadgeCount
                            count={debouncedUnreadCounts[channel.id]}
                          />
                        )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))
      )
      )}

      {/* Upcoming Live Sessions */}
      {communities[communityIndex].has_live_session === 1 && (
        <div
          className={styles.upcomingLiveSessionContainer}
          onClick={() => {
            localStorage.setItem(
              "communityId",
              String(communities[communityIndex].id),
            );
            setIsUpcomingLiveSessionScreen();
          }}
        >
          <span>CALENDAR - LIVE SESSIONS</span>
        </div>
      )}

      {/* Weekly Links */}
      <div
        className={styles.upcomingLiveSessionContainer}
        onClick={() => {
          localStorage.setItem(
            "communityId",
            String(communities[communityIndex].id),
          );
          setIsWeeklyLinksScreen();
        }}
      >
        <span>Weekly Links</span>
      </div>

      {/* Replay Sessions */}
      <div
        className={styles.upcomingLiveSessionContainer}
        onClick={() => {
          localStorage.setItem(
            "communityId",
            String(communities[communityIndex].id),
          );
          setIsReplaySessionsScreen();
        }}
      >
        <span>Replay Sessions</span>
      </div>
    </div>
  );
};

export default CommunityMenuPanel;
